import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import {
  PICKABLE_BIN_TYPES,
  REPLENISHMENT_SOURCE_LOCATION_TYPES,
} from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { useTranslation } from "@redotech/redo-i18n";
import EditIcon from "@redotech/redo-icons/edit-02.svg";
import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import type { InventoryItemId, LocationId } from "@redotech/redo-inventory-id";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { ProductId } from "@redotech/redo-product-id";
import type { StorageLocationId } from "@redotech/redo-warehouse-id";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { DisabledTooltip } from "@redotech/redo-web/arbiter-components/tooltip/disabled-tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import {
  AddBinAtLocationModal,
  type AddBinAtLocationTarget,
  uniqueBins,
} from "../shared/add-bin-at-location-modal";
import { binTypeColor, binTypeLabel } from "../shared/bin-type-label";
import { LotBadge } from "../shared/lot-badge";
import { ProductDisplay } from "../shared/product-display";
import {
  AdjustInventoryModal,
  type AdjustInventoryTarget,
} from "../storage-locations/adjust-inventory-modal";

const PICKABLE_BIN_TYPE_VALUES: ReadonlySet<string> = new Set(
  PICKABLE_BIN_TYPES,
);

export interface ProductBinsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBin: (bin: {
    id: StorageLocationId;
    name: string;
    available: number;
  }) => void;
  locationId: LocationId;
  productId: ProductId;
  productTitle: string;
  sku: string | null;
  imageUrl: string | null;
  lotNumber: string | null;
  inventoryItemId: InventoryItemId | null;
  currentBinId: StorageLocationId | null;
  purpose?: "destination" | "source" | "view" | "pick";
  pickableBinTypes?: readonly string[];
  lotExpiryBufferDays?: number;
  hideUnpickable?: boolean;
  neededQuantity?: number;
  /** Shows an adjust action per bin; requires the receive-order-updates permission. */
  allowAdjustInventory?: boolean;
  onInventoryAdjusted?: () => void;
  breakdown?: {
    pickableOnHand: number;
    overstockOnHand: number;
    receivingOnHand: number;
    unpickedItemTotal: number;
  };
}

interface BinLot {
  inventoryItemId: InventoryItemId | null;
  lotNumber: string | null;
  expirationDate: Date | null;
  quantity: number;
}

interface BinGroup {
  storageLocationId: StorageLocationId;
  binName: string;
  binType: string | null;
  totalQuantity: number;
  committedQuantity: number;
  lots: BinLot[];
  mixesLots: boolean;
  uncounted: boolean;
}

export function groupBinsForProduct(
  rows: {
    storageLocationId: StorageLocationId;
    binName: string;
    binType?: string | null;
    inventoryItemId?: InventoryItemId | null;
    lotNumber?: string | null;
    expirationDate?: Date | null;
    lastCountedAt?: Date | null;
    totalQuantity: number;
    committedQuantity: number;
  }[],
  inventoryItemId: InventoryItemId | null,
  suggestedBinId: StorageLocationId | null = null,
): BinGroup[] {
  const byBin = new Map<StorageLocationId, BinGroup>();
  for (const row of rows) {
    const group = byBin.get(row.storageLocationId) ?? {
      storageLocationId: row.storageLocationId,
      binName: row.binName,
      binType: row.binType ?? null,
      totalQuantity: 0,
      committedQuantity: 0,
      lots: [],
      mixesLots: false,
      uncounted: true,
    };
    group.totalQuantity += row.totalQuantity;
    if (row.lastCountedAt) {
      group.uncounted = false;
    }
    group.committedQuantity += row.committedQuantity;
    const rowItemId = row.inventoryItemId ?? null;
    group.lots.push({
      inventoryItemId: rowItemId,
      lotNumber: row.lotNumber ?? null,
      expirationDate: row.expirationDate ?? null,
      quantity: row.totalQuantity,
    });
    if (row.totalQuantity > 0 && rowItemId !== inventoryItemId) {
      group.mixesLots = true;
    }
    byBin.set(row.storageLocationId, group);
  }
  return [...byBin.values()].sort((a, b) => {
    const aSuggested = a.storageLocationId === suggestedBinId;
    const bSuggested = b.storageLocationId === suggestedBinId;
    if (aSuggested !== bSuggested) {
      return aSuggested ? -1 : 1;
    }
    const aStocked = a.totalQuantity > 0;
    const bStocked = b.totalQuantity > 0;
    if (aStocked !== bStocked) {
      return aStocked ? -1 : 1;
    }
    if (a.uncounted !== b.uncounted) {
      return a.uncounted ? 1 : -1;
    }
    if (a.totalQuantity !== b.totalQuantity) {
      return b.totalQuantity - a.totalQuantity;
    }
    return a.binName.localeCompare(b.binName);
  });
}

export const ProductBinsModal = memo(function ProductBinsModal({
  isOpen,
  onClose,
  onSelectBin,
  locationId,
  productId,
  productTitle,
  sku,
  imageUrl,
  lotNumber,
  inventoryItemId,
  currentBinId,
  purpose = "destination",
  pickableBinTypes,
  lotExpiryBufferDays,
  hideUnpickable = false,
  neededQuantity,
  allowAdjustInventory = false,
  onInventoryAdjusted,
  breakdown,
}: ProductBinsModalProps) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const isMobile = useScreenType(WMS_MOBILE_MAX_WIDTH) === ScreenType.MOBILE;
  const canAdjustInventory =
    useHasPermission(OrderManagementPermissions.RECEIVE_ORDER_UPDATES) &&
    allowAdjustInventory;
  const [adjustTarget, setAdjustTarget] =
    useState<AdjustInventoryTarget | null>(null);
  const [addBinTarget, setAddBinTarget] =
    useState<AddBinAtLocationTarget | null>(null);
  const [binsReloadKey, setBinsReloadKey] = useState(0);

  const binsLoad = useLoad(
    async (signal) => {
      if (!isOpen) {
        return null;
      }
      const result = await rpcClient.getBinsForProduct(
        {
          locationId,
          productId,
          includeStorage: true,
          includeReceiving: true,
          includeCommitted: true,
          includeEmpty: true,
        },
        { signal },
      );
      return result.bins;
    },
    [isOpen, rpcClient, locationId, productId, purpose, binsReloadKey],
  );

  const handleAdjustBin = (bin: BinGroup) => {
    const [lot, ...otherLots] = bin.lots;
    if (!lot || otherLots.length > 0) {
      return;
    }
    setAdjustTarget({
      storageLocationId: bin.storageLocationId,
      binName: bin.binName,
      item: {
        productInfo: { id: productId, title: productTitle, sku, imageUrl },
        inventoryItemId: lot.inventoryItemId,
        quantity: lot.quantity,
        committed: bin.committedQuantity,
        lotNumber: lot.lotNumber,
        expirationDate: lot.expirationDate,
      },
    });
  };

  const handleAddBin = () => {
    setAddBinTarget({
      locationId,
      locationName: null,
      existingBins: uniqueBins(binsLoad.value ?? []),
    });
  };

  const handleInventoryAdjusted = () => {
    setBinsReloadKey((key) => key + 1);
    onInventoryAdjusted?.();
  };

  const expiryCutoff = useMemo(() => {
    if (lotExpiryBufferDays === undefined) {
      return null;
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + Math.max(0, lotExpiryBufferDays));
    return cutoff;
  }, [lotExpiryBufferDays]);
  const isLotExpired = useCallback(
    (lot: BinLot) =>
      !!expiryCutoff &&
      !!lot.expirationDate &&
      lot.expirationDate <= expiryCutoff,
    [expiryCutoff],
  );
  const pickBlockReason = useCallback(
    (bin: BinGroup): string | undefined => {
      const liveLots = bin.lots.filter((lot) => !isLotExpired(lot));
      const unexpiredQuantity = liveLots.reduce(
        (sum, lot) => sum + lot.quantity,
        0,
      );
      const requiredLotExpired =
        !!inventoryItemId &&
        bin.lots.some(
          (lot) =>
            lot.inventoryItemId === inventoryItemId &&
            lot.quantity > 0 &&
            isLotExpired(lot),
        );
      const holdsThisLot = inventoryItemId
        ? liveLots.some(
            (lot) =>
              lot.inventoryItemId === inventoryItemId && lot.quantity > 0,
          )
        : unexpiredQuantity > 0;
      if (bin.binType && !PICKABLE_BIN_TYPE_VALUES.has(bin.binType)) {
        return t("Can't pick from this bin type");
      }
      if (bin.uncounted) {
        return t("This bin hasn't been counted yet");
      }
      if (requiredLotExpired) {
        return t("This lot has expired");
      }
      if (!inventoryItemId && bin.totalQuantity > 0 && unexpiredQuantity <= 0) {
        return t("Only expired lots here");
      }
      if (!holdsThisLot) {
        return inventoryItemId
          ? t("Doesn't hold the required item")
          : t("Nothing to pick here");
      }
      if (unexpiredQuantity - bin.committedQuantity <= 0) {
        return t("All stock here is reserved");
      }
      return undefined;
    },
    [inventoryItemId, isLotExpired, t],
  );

  const bins = useMemo(
    () =>
      groupBinsForProduct(
        binsLoad.value ?? [],
        inventoryItemId,
        currentBinId,
      ).filter(
        (bin) =>
          (!pickableBinTypes ||
            !bin.binType ||
            pickableBinTypes.includes(bin.binType)) &&
          (!hideUnpickable ||
            bin.storageLocationId === currentBinId ||
            !pickBlockReason(bin)),
      ),
    [
      binsLoad.value,
      inventoryItemId,
      currentBinId,
      pickableBinTypes,
      hideUnpickable,
      pickBlockReason,
    ],
  );
  const totalUnits = bins.reduce((sum, bin) => sum + bin.totalQuantity, 0);

  const sections = useMemo(() => {
    const order = ["bin", "storage", "receiving"];
    const byType = new Map<string, BinGroup[]>();
    for (const bin of bins) {
      const type =
        bin.binType && order.includes(bin.binType) ? bin.binType : "other";
      byType.set(type, [...(byType.get(type) ?? []), bin]);
    }
    return [...order, "other"]
      .filter((type) => byType.has(type))
      .map((type) => ({ type, bins: byType.get(type) ?? [] }));
  }, [bins]);

  const renderBin = (bin: BinGroup) => {
    const isCurrent = bin.storageLocationId === currentBinId;
    const liveLots = bin.lots.filter((lot) => !isLotExpired(lot));
    const unexpiredQuantity = liveLots.reduce(
      (sum, lot) => sum + lot.quantity,
      0,
    );
    const holdsThisLot = inventoryItemId
      ? liveLots.some(
          (lot) => lot.inventoryItemId === inventoryItemId && lot.quantity > 0,
        )
      : unexpiredQuantity > 0;
    const isBackstock =
      !!bin.binType &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (REPLENISHMENT_SOURCE_LOCATION_TYPES as readonly string[]).includes(
        bin.binType,
      );
    const isReceiving = bin.binType === "receiving";
    const pickBlockedMessage =
      purpose !== "pick" ? undefined : pickBlockReason(bin);
    const blocked =
      purpose === "pick"
        ? !!pickBlockedMessage
        : purpose === "source"
          ? !isBackstock || !holdsThisLot
          : isReceiving || bin.mixesLots;
    const blockedMessage = !blocked
      ? undefined
      : purpose === "pick"
        ? pickBlockedMessage
        : purpose === "source"
          ? !isBackstock
            ? t("Only backstock bins can be a source")
            : lotNumber
              ? t("Doesn't hold this lot")
              : t("Nothing to pull from here")
          : isReceiving
            ? t("Can't stock into a receiving bin")
            : lotNumber
              ? t("Holds a different lot of this product")
              : t("Holds lot-tracked stock of this product");
    return (
      <Flex
        align={isMobile && purpose !== "view" ? "stretch" : "center"}
        bgColor={isCurrent ? "secondary" : undefined}
        borderRadius="md"
        dir={isMobile && purpose !== "view" ? "column" : "row"}
        gap="sm"
        justify="space-between"
        key={bin.storageLocationId}
        p={purpose === "view" ? "xs" : isMobile ? "sm" : "md"}
      >
        <Flex dir="column" gap="xxs" style={{ minWidth: 0 }}>
          <Flex align="center" gap="sm" wrap="wrap">
            <Text fontSize="sm" fontWeight="semibold">
              {bin.binName}
            </Text>
            <RedoBadge
              color={binTypeColor(bin.binType)}
              size="xs"
              text={binTypeLabel(bin.binType)}
            />
            {isCurrent && (
              <RedoBadge color="blue" size="xs" text={t("Suggested")} />
            )}
            {bin.mixesLots && (
              <RedoBadge color="warning" size="xs" text={t("Lot mix")} />
            )}
            {bin.uncounted && (
              <RedoBadge color="warning" size="xs" text={t("Uncounted")} />
            )}
            {!bin.uncounted &&
              bin.totalQuantity === 0 &&
              purpose !== "view" && (
                <RedoBadge color="gray" size="xs" text={t("Empty")} />
              )}
            {bin.lots
              .filter(
                (lot) =>
                  lot.lotNumber &&
                  (!hideUnpickable || (lot.quantity > 0 && !isLotExpired(lot))),
              )
              .map((lot) => (
                <LotBadge
                  expirationDate={lot.expirationDate}
                  key={lot.inventoryItemId ?? lot.lotNumber}
                  lotNumber={lot.lotNumber!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                  quantity={lot.quantity}
                  size="xs"
                  type={
                    lot.inventoryItemId === inventoryItemId
                      ? "primary"
                      : "alternate"
                  }
                />
              ))}
          </Flex>
          <Text fontSize="xs" textColor="secondary">
            {bin.committedQuantity > 0
              ? t("Qty: {{qty}} ({{reserved}} reserved)", {
                  qty: bin.totalQuantity,
                  reserved: bin.committedQuantity,
                })
              : t("Qty: {{qty}}", { qty: bin.totalQuantity })}
          </Text>
          {purpose === "pick" &&
            neededQuantity !== undefined &&
            !pickBlockedMessage &&
            unexpiredQuantity - bin.committedQuantity < neededQuantity && (
              <Text fontSize="xs" fontWeight="semibold" textColor="warning">
                {t("Covers {{count}} of {{needed}}", {
                  count: Math.max(0, unexpiredQuantity - bin.committedQuantity),
                  needed: neededQuantity,
                })}
              </Text>
            )}
        </Flex>
        <Flex
          align="center"
          gap="xs"
          shrink={0}
          w={isMobile && purpose !== "view" ? "full" : undefined}
        >
          {canAdjustInventory && (
            <DisabledTooltip
              disabledMessage={
                bin.lots.length > 1
                  ? t(
                      "This bin holds several lots of this product. Adjust them from Storage locations.",
                    )
                  : undefined
              }
            >
              <RedoButton
                aria-label={t("Adjust inventory in {{bin}}", {
                  bin: bin.binName,
                })}
                data-dd-action-name="product_bins:adjust_bin_click"
                disabled={bin.lots.length > 1}
                hierarchy="tertiary"
                IconLeading={EditIcon}
                onClick={() => handleAdjustBin(bin)}
                size={isMobile ? "md" : "sm"}
              />
            </DisabledTooltip>
          )}
          {!isCurrent && purpose !== "view" && (
            <DisabledTooltip disabledMessage={blockedMessage}>
              <RedoButton
                disabled={blocked}
                flexGrow={isMobile ? 1 : undefined}
                hierarchy="secondary"
                onClick={() =>
                  onSelectBin({
                    id: bin.storageLocationId,
                    name: bin.binName,
                    available: Math.max(
                      0,
                      unexpiredQuantity - bin.committedQuantity,
                    ),
                  })
                }
                size={isMobile ? "md" : "sm"}
                text={
                  purpose === "pick"
                    ? t("Pick from bin")
                    : blocked && isMobile
                      ? purpose === "source"
                        ? t("Use bin (no stock)")
                        : isReceiving
                          ? t("Use bin (receiving)")
                          : t("Use bin (would mix lots)")
                      : t("Use bin")
                }
                theme="normal"
              />
            </DisabledTooltip>
          )}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <RedoModal
        isOpen={isOpen}
        modalSize="sm"
        onModalCloseRequested={onClose}
        secondaryButton={{
          text: t("Close"),
          onClick: onClose,
          size: isMobile ? "md" : undefined,
        }}
        title={
          purpose === "view" ? t("Stock details") : t("Where is this product?")
        }
        TitleIcon={isMobile ? undefined : MarkerPinIcon}
      >
        <Flex dir="column" gap="md" w="full">
          <ProductDisplay
            imageSize={isMobile ? "md" : "lg"}
            imageUrl={imageUrl}
            lotNumber={lotNumber}
            productTitle={productTitle}
            sku={sku}
          />
          {breakdown ? (
            <Flex dir="column" gap="sm" w="full">
              <Flex gap="sm" w="full">
                {[
                  { label: t("Pickable"), value: breakdown.pickableOnHand },
                  { label: t("Backstock"), value: breakdown.overstockOnHand },
                  { label: t("Receiving"), value: breakdown.receivingOnHand },
                ].map((stat) => (
                  <Flex
                    align="center"
                    basis={0}
                    bgColor="secondary"
                    borderRadius="md"
                    dir="column"
                    gap="none"
                    grow={1}
                    key={stat.label}
                    px="xs"
                    py="xs"
                  >
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      textColor={stat.value === 0 ? "tertiary" : "primary"}
                    >
                      {stat.value}
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      textColor="tertiary"
                    >
                      {stat.label}
                    </Text>
                  </Flex>
                ))}
              </Flex>
              <Text
                fontSize="sm"
                textColor={
                  breakdown.unpickedItemTotal > breakdown.pickableOnHand
                    ? "error"
                    : "secondary"
                }
              >
                {breakdown.unpickedItemTotal > breakdown.pickableOnHand
                  ? t(
                      "{{unpicked}} unpicked, short {{short}} in pickable bins",
                      {
                        unpicked: breakdown.unpickedItemTotal,
                        short:
                          breakdown.unpickedItemTotal -
                          breakdown.pickableOnHand,
                      },
                    )
                  : t("{{unpicked}} unpicked, covered by pickable bins", {
                      unpicked: breakdown.unpickedItemTotal,
                    })}
              </Text>
            </Flex>
          ) : (
            <Flex align="center" gap="sm" justify="space-between" wrap="wrap">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Bin locations")} ({bins.length})
              </Text>
              {bins.length > 0 && (
                <Text fontSize="xs" textColor="secondary">
                  {t("{{qty}} units on hand", { qty: totalUnits })}
                </Text>
              )}
            </Flex>
          )}

          {binsLoad.pending ? (
            <Flex align="center" justify="center" p="xl">
              <RedoSpinner size="md" />
            </Flex>
          ) : binsLoad.error ? (
            <Text fontSize="sm" textColor="error">
              {t("Could not load bin locations.")}
            </Text>
          ) : bins.length === 0 ? (
            <Text fontSize="sm" textColor="secondary">
              {t("This product is not stocked anywhere in this warehouse yet.")}
            </Text>
          ) : (
            <Flex dir="column" gap="xs" maxHeight="half-screen" overflow="auto">
              {purpose === "view"
                ? sections.map((section) => (
                    <Flex dir="column" gap="xs" key={section.type} pt="xs">
                      <Flex align="center" gap="sm">
                        <RedoBadge
                          color={binTypeColor(section.type)}
                          size="xs"
                          text={binTypeLabel(section.type)}
                        />
                        <Text fontSize="xs" textColor="secondary">
                          {t("{{count}} bin(s) · {{qty}} units", {
                            count: section.bins.length,
                            qty: section.bins.reduce(
                              (sum, bin) => sum + bin.totalQuantity,
                              0,
                            ),
                          })}
                        </Text>
                      </Flex>
                      {section.bins.map(renderBin)}
                    </Flex>
                  ))
                : bins.map(renderBin)}
            </Flex>
          )}
          {canAdjustInventory && !binsLoad.pending && !binsLoad.error && (
            <Flex>
              <RedoButton
                data-dd-action-name="product_bins:add_bin_click"
                hierarchy="secondary"
                IconLeading={PlusIcon}
                onClick={handleAddBin}
                size={isMobile ? "md" : "sm"}
                text={t("Add to a new bin")}
              />
            </Flex>
          )}
        </Flex>
      </RedoModal>
      <AddBinAtLocationModal
        lotTracked={(binsLoad.value ?? []).some((bin) => !!bin.inventoryItemId)}
        onClose={() => setAddBinTarget(null)}
        onSuccess={handleInventoryAdjusted}
        productId={productId}
        productTitle={productTitle}
        target={addBinTarget}
      />
      <AdjustInventoryModal
        onClose={() => setAdjustTarget(null)}
        onSuccess={handleInventoryAdjusted}
        rpcClient={rpcClient}
        target={adjustTarget}
      />
    </>
  );
});
